import { Grid } from '@mui/material';
import { differenceInDays } from 'date-fns';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import {
  useDictionary,
  useIsDesktop,
  useMedications,
  usePrescriptionAndUnHandledOrders,
} from 'Hooks';
import {
  CreateMedicineIcon,
  SubmitApplication,
  UniversalRequestSubject7,
} from 'Images';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { goToRenewPrescription } from 'Utils';
import { AlertDialogOrders } from '../../AlertDialogOrders';
import { DialogOrderRegularMedications } from '../../DialogOrderRegularMedications';
import { sendEvent } from 'Utils/sendEvent';
import { ButtonActionNew } from 'Components/ButtonActionNew';
import { SuccessOrderDialog } from 'Modules/EquipmentCreateOrder/SuccessOrderDialog';
import { exportToPdf } from 'API';

interface IButtonActionsProps {
  handleNoValidMedications: () => void;
}

export const ButtonActions = (props: IButtonActionsProps) => {
  const isDesktop = useIsDesktop();

  const navigate = useNavigate();

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );

  const { medications } = useMedications();
  const hasValidMedications = medications.length > 0;

  const {
    prescriptionsAndUnHandledOrders: { prescriptions },
    STATUS,
  } = usePrescriptionAndUnHandledOrders();

  const [openAlertDialog, setOpenAlertDialog] = useState<boolean>(false);
  const [openSuccessOrderDialog, setOpenSuccessOrderDialog] =
    useState<boolean>(false);
  const [
    openOrderRegularMedicationsDialog,
    setOpenOrderRegularMedicationsDialog,
  ] = useState<boolean>(false);

  const [
    successOrderDialogExpiredPrescriptions,
    setSuccessOrderDialogExpiredPrescriptions,
  ] = useState<string[] | undefined>();

  const handleCloseOrderRegularMedicationsDialog = () => {
    setOpenOrderRegularMedicationsDialog(false);
  };

  const handleCloseSuccessOrderDialog = () => {
    setOpenSuccessOrderDialog(false);
    setSuccessOrderDialogExpiredPrescriptions(undefined);
  };

  const handleOpenRegularMedicationsDialog = () => {
    if (hasValidMedications) {
      setOpenOrderRegularMedicationsDialog(true);
    } else {
      props.handleNoValidMedications();
    }
  };

  const handleCloseAlertDialog = () => {
    setOpenAlertDialog(false);
  };
  const openDialogHandler = () => {
    sendEvent('Medicine', 'MedicineOrder');
    if (prescriptions.length > 0) {
      const numOfDays = isDesktop
        ? differenceInDays(new Date(), new Date(prescriptions[0].createDate))
        : differenceInDays(
            new Date(),
            new Date(prescriptions[prescriptions.length - 1].createDate)
          );

      if (numOfDays <= 3 && numOfDays >= 0) {
        //Between 0-3 days
        setOpenAlertDialog(true);
        return;
      }
    }
    if (hasValidMedications) {
      navigate('/medicine/createOrder');
    } else {
      props.handleNoValidMedications();
    }
  };

  const openSuccessOrderDialogHandler = () => {
    setOpenSuccessOrderDialog(true);
  };

  const exportToPdfHandler = async () => {
    sendEvent('Medicine', 'MedicineReport');
    await exportToPdf();
  };

  const showButtons = STATUS === 'FINISHED' || STATUS === 'FAILED';

  const customStyleBtn = {
    borderColor: 'primary.shade_400',
    width: isDesktop ? '205px' : '105px',
    p: isDesktop ? '16px 12px' : '12px 8px',
    boxShadow: '0px 1px 22px 0px rgba(21, 82, 232, 0.12)',
  };

  const lastOrder = prescriptions[0];
  const numberOfItems = lastOrder?.medications
    ? lastOrder.medications?.length
    : 0;

  return (
    <>
      {showButtons && (
        <AlertDialogOrders
          open={openAlertDialog}
          handleClose={handleCloseAlertDialog}
          openRegularMedicationsDialog={handleOpenRegularMedicationsDialog}
        />
      )}
      <DialogOrderRegularMedications
        open={openOrderRegularMedicationsDialog}
        handleClose={handleCloseOrderRegularMedicationsDialog}
        openSuccessOrderDialog={openSuccessOrderDialogHandler}
        setSuccessOrderDialogExpiredPrescriptions={
          setSuccessOrderDialogExpiredPrescriptions
        }
      />

      <SuccessOrderDialog
        handleClose={handleCloseSuccessOrderDialog}
        numberOfItems={numberOfItems}
        open={openSuccessOrderDialog}
        renewPrescription={successOrderDialogExpiredPrescriptions}
        link={'/medicine'}
        linkLabel={dictionary.medicineLineLabel}
      />
      {showButtons && (
        <Grid
          justifyContent={isDesktop ? 'flex-start' : 'space-between'}
          container
          alignItems='center'
          spacing={isDesktop ? 3 : 1.25}
          mt={1}
          mb={3}
          flexWrap='nowrap'
        >
          <Grid item>
            <ButtonActionNew
              imageSrc={UniversalRequestSubject7}
              text={dictionary.requestRegularMedications}
              callback={openDialogHandler}
              role='link'
              sx={customStyleBtn}
            />
          </Grid>
          <Grid item>
            <ButtonActionNew
              imageSrc={SubmitApplication}
              text={dictionary.requestNewMedication}
              callback={goToRenewPrescription}
              role='link'
              sx={customStyleBtn}
            />
          </Grid>
          <Grid item>
            <ButtonActionNew
              imageSrc={CreateMedicineIcon}
              text={dictionary.exportToPDF}
              callback={exportToPdfHandler}
              role='link'
              sx={customStyleBtn}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};
